<script setup lang="ts">
import {computed} from "vue";
import type {NewsInterface} from "~~/server/types/NewsInterface";
import {useI18n} from "vue-i18n";
import {localDateFormat} from "~/services/helpers";

const route = useRoute()
const { locale } = useI18n();

const newStore=useNewsStore();

const allNews=computed(()=>newStore.list);
const news_slug=computed(()=>route.params.category_slug+'/'+route.params.news_slug);
const key_product = computed(() => 'news:'+news_slug.value + ':' + locale.value)
const { data, error } = await useAsyncData<NewsInterface>(
    key_product,
    () => {
      if (allNews.value.length) {
        const foundProduct = allNews.value.find(
            i => i.url === news_slug.value && i?.loaded
        );
        if (foundProduct) {
          return Promise.resolve(foundProduct as NewsInterface);
        }
      }
      return newStore.loadSingleNews(news_slug.value)
    }
);
if (error.value) {
  throw createError(error.value);
  }
const news=computed<NewsInterface|undefined>(()=>data.value);


onMounted(async () => {
  await Promise.all([
    loadOtherNews(),
    newStore.loadCategories(),
    newStore.loadNews(),
  ])
})

const other=ref<NewsInterface[]>([]);

const otherLoading=ref(false);
const loadOtherNews=async () => {
  const {$api} = useNuxtApp();
    otherLoading.value=true;
    try {
      other.value=[];
      const response = await $api<{ list: NewsInterface[] }>('blog/by/'+ news.value?.category?.url, {
        method: 'GET',
        params: { limit: 4}
      });
      other.value = response.list;
    }
    finally {
      otherLoading.value=false;
}
}
usePageSeo(computed(() => ({
  ...news.value!,
  schemaType: 'article',
  publishedAt: news.value?.news_date || news.value?.created_at,
  modifiedAt: news.value?.updated_at,
})));

</script>

<template>
  <section class="container-xxl product-page single-news">

    <!-- Breadcrumb -->
    <nav aria-label="Breadcrumb" class="pt-0 pt-lg-1 mb-3">
      <el-breadcrumb separator="/" class="m-0">
        <el-breadcrumb-item :to="{ path: '/' }">
          {{ $t('home_page') }}
        </el-breadcrumb-item>

        <el-breadcrumb-item
            :to="{ path: news?.category?.real_url }"
        >
          {{ news?.category?.name }}
        </el-breadcrumb-item>

        <el-breadcrumb-item aria-current="page">
          {{ news?.name }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </nav>

    <div class="row">

      <!-- ARTICLE -->
      <div class="col-lg-9">
        <article v-if="news" class="article">

          <header class="article-header">
            <h1 class="title-news fs-3 pt-2 pb-2">
              {{ news?.name }}
            </h1>

            <div class="meta pb-3">
              <span class="meta-label">
                {{ $t("order_date") }}:
              </span>

              <time :datetime="news?.news_date">
                {{ localDateFormat(news?.news_date) }}
              </time>
            </div>
          </header>

          <figure class="hero">
            <img
                class="single-image-news"
                :src="news?.photo"
                :alt="news?.name || 'Article image'"
                :title="news?.name || ''"
                loading="eager"
                decoding="async"
            />
          </figure>

          <div
              class="article-content pt-5"
              v-html="news?.description"
          ></div>

        </article>

        <el-skeleton v-else :rows="8" animated />
      </div>

      <!-- RELATED -->
      <aside
          class="col-lg-3 sticky"
          aria-label="Related articles"
      >
        <h2 class="aside-title fs-1 text-center">
          {{ $t('others') }}
          <span v-if="news?.category?.name" class="aside-sub">
            {{ news?.category?.name }}
          </span>
        </h2>

        <el-divider class="my-2" border-style="dashed" />

        <el-skeleton v-if="otherLoading" :rows="4" animated />

        <ul v-else class="related-list">
          <li
              v-for="item of other"
              :key="item.id || item.url"
              class="related-item"
          >
            <NuxtLinkLocale
                :to="item.url"
                class="related-link"
            >
              <img
                  :src="item.icon || item.photo"
                  :alt="item.name"
                  loading="lazy"
                  decoding="async"
                  class="related-image"
              />

              <div class="related-content">
                <div class="related-title">
                  {{ item.name }}
                </div>

                <p class="related-description">
                  {{ item.short_description }}
                </p>
              </div>
            </NuxtLinkLocale>
          </li>
        </ul>

      </aside>
    </div>
  </section>
</template>

<style scoped>

/* Layout */
.single-news {
  padding-bottom: 32px;
}

.article {
  max-width: 920px;
}

/* Meta */
.meta {
  font-size: var(--fs--1);
  opacity: 0.85;
  display: flex;
  gap: 6px;
  align-items: center;
}

/* Hero image */
.single-image-news {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid var(--el-color-info-light-8);
  background: #f8f8f8;
}

/* Article content */
.article-content :deep(p) {
  line-height: 1.7;
  margin-bottom: 14px;
}

.article-content :deep(h2),
.article-content :deep(h3) {
  margin-top: 24px;
  margin-bottom: 12px;
}

.article-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
}

/* Sticky */
.sticky {
  position: sticky;
  top: 1.5rem;
  align-self: flex-start;
}

@media (max-width: 991px) {
  .sticky {
    position: static;
    margin-top: 24px;
  }
}

/* Related */
.related-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.related-item {
  margin-bottom: 14px;
}

.related-link {
  display: flex;
  gap: 14px;
  padding: 10px;
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease, transform 0.15s ease;
}

.related-link:hover {
  background: rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.related-image {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.related-content {
  min-width: 0;
}

.related-title {
  font-weight: var(--fw-semibold);
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-description {
  margin: 4px 0 0 0;
  font-size: var(--fs--2);
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.aside-title {
  margin-bottom: 0;
}

.aside-sub {
  display: block;
  font-size: var(--fs--1);
  opacity: 0.8;
}

</style>
